/**
 * @author qiao / https://github.com/qiao
 * @author mrdoob / http://mrdoob.com
 * @author alteredq / http://alteredqualia.com/
 * @author WestLangley / http://github.com/WestLangley
 * @author erich666 / http://erichaines.com
 *
 * Modified by Higharc
 */
import { aabb2ByTwoPoints, aabb2Grow } from "geom/aabb2";
import type { AABB3 } from "geom/aabb3Types";
import type { Matrix4, OrthographicCamera, PerspectiveCamera, BaseEvent } from "three";
import { MOUSE, Quaternion, Spherical, Vector2, Vector3 } from "three";
import { CanvasControls } from "./CanvasControls";
import { htmlElementIsGUI } from "./htmlElementIsGUI";

const CHANGE_EVENT: BaseEvent<"change"> = { type: "change" };
const PROJECTION_CHANGE_EVENT: BaseEvent<"projectionChange"> = { type: "projectionChange" };
const START_EVENT: BaseEvent<"start"> = { type: "start" };
const END_EVENT: BaseEvent<"end"> = { type: "end" };
const STATE = { NONE: -1, ROTATE: 0, DOLLY: 1, PAN: 2, TOUCH_ROTATE: 3, TOUCH_DOLLY_PAN: 4 };
const EPS = 0.000001;

const ORTHO_POLE_EPS = 1e-6;

/**
 * Converts a view direction (camera to target, unit vector) to spherical angles.
 * Polar = 0 is top-down (0,0,-1), polar = PI is bottom-up (0,0,1).
 * When direction is (0,0,±1), azimuth is derived from the up vector (atan2(upY, upX)).
 */
export function orthoViewDirectionToSpherical(
  dirX: number,
  dirY: number,
  dirZ: number,
  upX: number,
  upY: number,
  _upZ: number,
): { azimuth: number; polar: number } {
  const polar = Math.acos(Math.max(-1, Math.min(1, -dirZ)));
  const sinPolar = Math.sin(polar);
  let azimuth: number;
  if (sinPolar < ORTHO_POLE_EPS) {
    azimuth = Math.atan2(upY, upX);
  } else {
    azimuth = Math.atan2(dirY, dirX);
  }
  return { azimuth, polar };
}

/**
 * Converts spherical angles to a unit view direction (camera to target).
 * Polar = 0 => (0,0,-1), polar = PI => (0,0,1).
 */
export function orthoSphericalToViewDirection(
  azimuth: number,
  polar: number,
): { dirX: number; dirY: number; dirZ: number } {
  const sinPolar = Math.sin(polar);
  return {
    dirX: sinPolar * Math.cos(azimuth),
    dirY: sinPolar * Math.sin(azimuth),
    dirZ: -Math.cos(polar),
  };
}

/**
 * This set of controls performs orbiting, dollying (zooming), and panning.
 * Unlike TrackballControls, it maintains the "up" direction object.up (+Y by default).
 *
 * - Orbit - left mouse / touch: one-finger move
 * - Zoom - middle mouse, or mousewheel / touch: two-finger spread or squish
 * - Pan - right mouse, left mouse + ctrl/meta/shiftKey / touch: two-finger move
 */
export default class OrthographicControls extends CanvasControls {
  constructor(
    object: OrthographicCamera | PerspectiveCamera,
    domElement: HTMLElement | undefined | null,
    targetX: number,
    targetY: number,
    targetZ: number,
    onChangeEvent?: string,
    ..._moreArgs: unknown[]
  ) {
    super(domElement ?? document, object, new Vector3(targetX, targetY, targetZ));
    this.enableRotate = false;
    this.panConstraintPadding = 200;
    this.target0 = this.target.clone();
    this.position0 = this.object.position.clone();
    this.zoom0 = this.object.zoom;

    this.#onChangeEvent = onChangeEvent;
    this.#updateQuat = new Quaternion().setFromUnitVectors(object.up, new Vector3(0, 1, 0));
    this.#updateQuatInverse = this.#updateQuat.clone().invert();

    this.addDomElementListeners();
    this.update();
  }

  panConstraintPadding: number;
  target0: Vector3;
  position0: Vector3;
  zoom0: number;

  override get domElement(): HTMLElement {
    return this._domElement as HTMLElement;
  }

  override set domElement(value: HTMLElement | Document | undefined | null) {
    super.domElement = value;
  }

  #onChangeEvent: string | undefined;
  #state = STATE.NONE;
  #spherical = new Spherical();
  #sphericalDelta = new Spherical();
  #scale = 1;
  #panOffset = new Vector3();
  #zoomChanged = false;
  #updateOffset = new Vector3();
  #updateQuat = new Quaternion();
  #updateQuatInverse = new Quaternion();
  #lastPosition = new Vector3();
  #lastQuaternion = new Quaternion();
  #rotateStart = new Vector2();
  #rotateEnd = new Vector2();
  #rotateDelta = new Vector2();
  #panStart = new Vector2();
  #panEnd = new Vector2();
  #panDelta = new Vector2();
  #dollyFixedStart = new Vector2();
  #dollyStart = new Vector2();
  #dollyEnd = new Vector2();
  #dollyDelta = new Vector2();

  // World space delta
  #panOffsetHorizontal = new Vector3();
  #panOffsetVertical = new Vector3();
  #oldViewPos = new Vector3();
  #newViewPos = new Vector3();
  #corner1 = new Vector3();
  #corner2 = new Vector3();
  #dollyV = new Vector3();
  #dollyFocusWorld = new Vector3();
  #dollyOffset = new Vector3();

  #onContextMenuListener = (event: MouseEvent) => this.#onContextMenu(event);
  #onMouseDownListener = (event: MouseEvent) => this.#onMouseDown(event);
  #onMouseMoveListener = (event: MouseEvent) => this.#onMouseMove(event);
  #onMouseUpListener = () => this.#onMouseUp();
  #dragDocument: Document | null = null;
  #onMouseWheelListener = (event: WheelEvent) => this.#onMouseWheel(event);
  #onTouchStartListener = (event: TouchEvent) => this.#onTouchStart(event);
  #onTouchEndListener = () => this.#onTouchEnd();
  #onTouchMoveListener = (event: TouchEvent) => this.#onTouchMove(event);

  zoomTowardsCentre(): void {
    const element =
      this._domElement === document ? (this._domElement as Document).body : (this._domElement as HTMLElement);
    const rect = element.getBoundingClientRect();
    const x = (rect.left + rect.right) * 0.5;
    const y = (rect.top + rect.bottom) * 0.5;
    this.#dolly(1.0 / this.#getZoomScale(), x, y);
    this.update();
  }

  zoomOutFromCentre(): void {
    const element =
      this._domElement === document ? (this._domElement as Document).body : (this._domElement as HTMLElement);
    const rect = element.getBoundingClientRect();
    const x = (rect.left + rect.right) * 0.5;
    const y = (rect.top + rect.bottom) * 0.5;
    this.#dolly(this.#getZoomScale(), x, y);
    this.update();
  }

  getPolarAngle(): number {
    return this.#spherical.phi;
  }

  getAzimuthalAngle(): number {
    return this.#spherical.theta;
  }

  saveState(): void {
    this.target0.copy(this.target);
    this.position0.copy(this.object.position);
    this.zoom0 = this.object.zoom;
  }

  isAtZoomLimit(): { in: boolean; out: boolean } {
    const zoom = this.object.zoom;
    const eps = 1e-3;
    return {
      in: zoom >= this.maxZoom - eps,
      out: zoom <= this.minZoom + eps,
    };
  }

  getZoomFraction(): number {
    if (this.zoom0 <= 0) {
      return 1;
    }
    return this.object.zoom / this.zoom0;
  }

  reset(): void {
    this.target.copy(this.target0);
    this.object.position.copy(this.position0);
    this.object.zoom = this.zoom0;
    this.object.updateProjectionMatrix();
    this.dispatchEvent(PROJECTION_CHANGE_EVENT);
    this.dispatchEvent(CHANGE_EVENT);
    this.update();
    this.#state = STATE.NONE;
  }

  update(): boolean {
    const position = this.object.position;

    this.#updateOffset.copy(position).sub(this.target);
    this.#updateOffset.applyQuaternion(this.#updateQuat);
    this.#spherical.setFromVector3(this.#updateOffset);

    if (this.autoRotate && this.#state === STATE.NONE) {
      this.#rotateLeft(this.getAutoRotationAngle());
    }

    this.#spherical.theta += this.#sphericalDelta.theta;
    this.#spherical.phi += this.#sphericalDelta.phi;
    this.#spherical.theta = Math.max(this.minAzimuthAngle, Math.min(this.maxAzimuthAngle, this.#spherical.theta));
    this.#spherical.phi = Math.max(this.minPolarAngle, Math.min(this.maxPolarAngle, this.#spherical.phi));
    this.#spherical.makeSafe();

    this.#spherical.radius *= this.#scale;
    this.#spherical.radius = Math.max(this.minDistance, Math.min(this.maxDistance, this.#spherical.radius));

    this.target.add(this.#panOffset);

    this.#updateOffset.setFromSpherical(this.#spherical);
    this.#updateOffset.applyQuaternion(this.#updateQuatInverse);
    position.copy(this.target).add(this.#updateOffset);

    this.object.lookAt(this.target);

    if (this.enableDamping === true) {
      this.#sphericalDelta.theta *= 1 - this.dampingFactor;
      this.#sphericalDelta.phi *= 1 - this.dampingFactor;
      this.#panOffset.multiplyScalar(1 - this.dampingFactor);
    } else {
      this.#sphericalDelta.set(0, 0, 0);
      this.#panOffset.set(0, 0, 0);
    }

    this.#scale = 1;

    if (
      this.#zoomChanged ||
      this.#lastPosition.distanceToSquared(this.object.position) > EPS ||
      8 * (1 - this.#lastQuaternion.dot(this.object.quaternion)) > EPS
    ) {
      this.object.updateProjectionMatrix();
      this.dispatchEvent(PROJECTION_CHANGE_EVENT);
      this.dispatchEvent(CHANGE_EVENT);

      this.#lastPosition.copy(this.object.position);
      this.#lastQuaternion.copy(this.object.quaternion);
      this.#zoomChanged = false;

      // Enables listening to camera.position updates in other layers that need to move in sync
      if (this.#onChangeEvent) {
        const event = new CustomEvent(this.#onChangeEvent);
        globalThis.document?.dispatchEvent?.(event);
      }

      return true;
    }

    return false;
  }

  addDomElementListeners(): void {
    this.domElement.addEventListener("contextmenu", this.#onContextMenuListener, false);
    this.domElement.addEventListener("mousedown", this.#onMouseDownListener, false);
    this.domElement.addEventListener("wheel", this.#onMouseWheelListener, false);
    this.domElement.addEventListener("touchstart", this.#onTouchStartListener, false);
    this.domElement.addEventListener("touchend", this.#onTouchEndListener, false);
    this.domElement.addEventListener("touchmove", this.#onTouchMoveListener, false);
  }

  removeDomElementListeners(): void {
    this.domElement.removeEventListener("contextmenu", this.#onContextMenuListener, false);
    this.domElement.removeEventListener("mousedown", this.#onMouseDownListener, false);
    this.domElement.removeEventListener("wheel", this.#onMouseWheelListener, false);
    this.domElement.removeEventListener("touchstart", this.#onTouchStartListener, false);
    this.domElement.removeEventListener("touchend", this.#onTouchEndListener, false);
    this.domElement.removeEventListener("touchmove", this.#onTouchMoveListener, false);
  }

  dispose(): void {
    this.removeDomElementListeners();
    this.#removeDragListeners();
  }

  #getZoomScale(): number {
    return Math.pow(0.95, this.zoomSpeed);
  }

  #rotateLeft(angle: number): void {
    this.#sphericalDelta.theta -= angle;
  }

  #rotateUp(angle: number): void {
    this.#sphericalDelta.phi -= angle;
  }

  #panLeft(distance: number, objectMatrix: Matrix4, vOut: Vector3): void {
    vOut.setFromMatrixColumn(objectMatrix, 0);
    vOut.multiplyScalar(-distance);
  }

  #panUp(distance: number, objectMatrix: Matrix4, vOut: Vector3): void {
    if (this.screenSpacePanning === true) {
      vOut.setFromMatrixColumn(objectMatrix, 1);
    } else {
      vOut.setFromMatrixColumn(objectMatrix, 0);
      vOut.crossVectors(this.object.up, vOut);
    }
    vOut.multiplyScalar(distance);
  }

  // deltaX and deltaY are in pixels; right and down are positive
  #pan(screenDeltaX: number, screenDeltaY: number): void {
    const element: HTMLElement =
      this._domElement === document ? (this._domElement as Document).body : (this._domElement as HTMLElement);

    const cam = this.object as OrthographicCamera & { isOrthographicCamera?: boolean };
    if (cam.isOrthographicCamera) {
      this.#panLeft(
        (screenDeltaX * (cam.right - cam.left)) / cam.zoom / element.clientWidth,
        this.object.matrixWorld,
        this.#panOffsetHorizontal,
      );
      this.#panUp(
        (screenDeltaY * (cam.top - cam.bottom)) / cam.zoom / element.clientHeight,
        this.object.matrixWorld,
        this.#panOffsetVertical,
      );

      this.#panOffset.add(this.#panOffsetHorizontal);
      this.#panOffset.add(this.#panOffsetVertical);

      if (this.panContainBox) {
        if (!(this.screenSpacePanning)) {
          console.assert(false);
        }

        const panContainBox: AABB3 = this.panContainBox;
        const panConstraintPadding: number = this.panConstraintPadding;
        this.#oldViewPos.copy(this.target);
        this.#oldViewPos.applyMatrix4(this.object.matrixWorldInverse);
        this.#newViewPos.copy(this.target);
        this.#newViewPos.add(this.#panOffset);
        this.#newViewPos.applyMatrix4(this.object.matrixWorldInverse);

        const projectionHalfWidth = (cam.right - cam.left) / 2 / cam.zoom;
        const projectionHalfHeight = (cam.top - cam.bottom) / 2 / cam.zoom;

        this.#corner1.set(panContainBox.minX, panContainBox.minY, panContainBox.minZ);
        this.#corner1.applyMatrix4(this.object.matrixWorldInverse);
        this.#corner2.set(panContainBox.maxX, panContainBox.maxY, panContainBox.maxZ);
        this.#corner2.applyMatrix4(this.object.matrixWorldInverse);

        const viewAabb2 = aabb2ByTwoPoints(this.#corner1.x, this.#corner1.y, this.#corner2.x, this.#corner2.y);

        aabb2Grow(viewAabb2, projectionHalfWidth, projectionHalfHeight);
        aabb2Grow(viewAabb2, -panConstraintPadding / cam.zoom, -panConstraintPadding / cam.zoom);

        this.#newViewPos.x = Math.min(this.#newViewPos.x, Math.max(viewAabb2.maxX, this.#oldViewPos.x));
        this.#newViewPos.x = Math.max(this.#newViewPos.x, Math.min(viewAabb2.minX, this.#oldViewPos.x));
        this.#newViewPos.y = Math.min(this.#newViewPos.y, Math.max(viewAabb2.maxY, this.#oldViewPos.y));
        this.#newViewPos.y = Math.max(this.#newViewPos.y, Math.min(viewAabb2.minY, this.#oldViewPos.y));

        this.#newViewPos.applyMatrix4(this.object.matrixWorld);

        this.#panOffset.subVectors(this.#newViewPos, this.target);
      }
    } else {
      console.warn("WARNING: OrtographicControls.ts encountered an unknown camera type - pan disabled.");
      this.enablePan = false;
    }
  }

  #dolly(dollyScale: number, clientX?: number, clientY?: number): void {
    const cam = this.object as OrthographicCamera;
    if (cam.isOrthographicCamera) {
      const element: HTMLElement =
        this._domElement === document ? (this._domElement as Document).body : (this._domElement as HTMLElement);
      let viewFocusX = 0;
      let viewFocusY = 0;
      if (clientX !== undefined && clientY !== undefined) {
        const clientRect = element.getBoundingClientRect();
        viewFocusX = (clientX - clientRect.left) / clientRect.width;
        viewFocusY = 1 - (clientY - clientRect.top) / clientRect.height;

        const spaceFocusX = (viewFocusX * (cam.right - cam.left) + cam.left) / cam.zoom;
        const spaceFocusY = (viewFocusY * (cam.top - cam.bottom) + cam.bottom) / cam.zoom;

        this.#dollyOffset.copy(this.object.position).sub(this.target);
        this.#dollyFocusWorld.copy(this.target);
        this.#dollyV.setFromMatrixColumn(this.object.matrixWorld, 0);
        this.#dollyV.multiplyScalar(spaceFocusX);
        this.#dollyFocusWorld.add(this.#dollyV);
        this.#dollyV.setFromMatrixColumn(this.object.matrixWorld, 1);
        this.#dollyV.multiplyScalar(spaceFocusY);
        this.#dollyFocusWorld.add(this.#dollyV);
      }

      this.object.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.object.zoom * dollyScale));
      this.#zoomChanged = true;

      if (clientX !== undefined && clientY !== undefined) {
        const spaceFocusX = (viewFocusX * (cam.right - cam.left) + cam.left) / this.object.zoom;
        const spaceFocusY = (viewFocusY * (cam.top - cam.bottom) + cam.bottom) / this.object.zoom;

        this.#dollyV.setFromMatrixColumn(this.object.matrixWorld, 0);
        this.#dollyV.multiplyScalar(spaceFocusX);
        this.#dollyFocusWorld.sub(this.#dollyV);
        this.#dollyV.setFromMatrixColumn(this.object.matrixWorld, 1);
        this.#dollyV.multiplyScalar(spaceFocusY);
        this.#dollyFocusWorld.sub(this.#dollyV);
        this.target.copy(this.#dollyFocusWorld);
        this.object.position.copy(this.target).add(this.#dollyOffset);
      }
    } else {
      console.warn("WARNING: OrtographicControls.ts encountered an unknown camera type - dolly/zoom disabled.");
      this.enableZoom = false;
    }
  }

  #getTouchXY(touches: TouchList): { x: number; y: number } {
    switch (touches.length) {
      case 1: {
        const touch = touches.item(0);
        return touch ? { x: touch.clientX, y: touch.clientY } : { x: 0, y: 0 };
      }
      case 2: {
        // two-fingered touch: dolly-pan
        const { pageX: x1 = 0, pageY: y1 = 0 } = touches[0];
        const { pageX: x2 = 0, pageY: y2 = 0 } = touches[1];
        return {
          x: 0.5 * (x1 + x2),
          y: 0.5 * (y1 + y2),
        };
      }
    }
    return { x: 0, y: 0 };
  }

  #handleMouseDownRotate(_event: MouseEvent): void {
    this.#rotateStart.set(_event.clientX, _event.clientY);
  }

  #handleMouseDownPan(event: MouseEvent): void {
    this.#panStart.set(event.clientX, event.clientY);
  }

  #handleMouseDownDolly(event: MouseEvent): void {
    this.#dollyStart.set(event.clientX, event.clientY);
    this.#dollyFixedStart.set(event.clientX, event.clientY);
  }

  #handleMouseMoveRotate(event: MouseEvent): void {
    this.#rotateEnd.set(event.clientX, event.clientY);
    this.#rotateDelta.subVectors(this.#rotateEnd, this.#rotateStart).multiplyScalar(this.rotateSpeed);
    const element: HTMLElement =
      this._domElement === document ? (this._domElement as Document).body : (this._domElement as HTMLElement);
    this.#rotateLeft((2 * Math.PI * this.#rotateDelta.x) / element.clientHeight);
    this.#rotateUp((2 * Math.PI * this.#rotateDelta.y) / element.clientHeight);
    this.#rotateStart.copy(this.#rotateEnd);
    this.update();
  }

  #handleMouseMoveDolly(_event: MouseEvent): void {
    this.#dollyEnd.set(_event.clientX, _event.clientY);
    this.#dollyDelta.subVectors(this.#dollyEnd, this.#dollyStart);

    if (this.#dollyDelta.y > 0) {
      this.#dolly(this.#getZoomScale(), this.#dollyFixedStart.x, this.#dollyFixedStart.y);
    } else if (this.#dollyDelta.y < 0) {
      this.#dolly(1.0 / this.#getZoomScale(), this.#dollyFixedStart.x, this.#dollyFixedStart.y);
    }

    this.#dollyStart.copy(this.#dollyEnd);
    this.update();
  }

  #handleMouseMovePan(event: MouseEvent): void {
    this.#panEnd.set(event.clientX, event.clientY);
    this.#panDelta.subVectors(this.#panEnd, this.#panStart).multiplyScalar(this.panSpeed);
    this.#pan(this.#panDelta.x, this.#panDelta.y);
    this.#panStart.copy(this.#panEnd);
    this.update();
  }

  #handleMouseWheel(event: WheelEvent): void {
    if (event.deltaY < 0) {
      this.#dolly(1.0 / this.#getZoomScale(), event.clientX, event.clientY);
    } else if (event.deltaY > 0) {
      this.#dolly(this.#getZoomScale(), event.clientX, event.clientY);
    }
    this.update();
  }

  #handleTouchStartDollyPan(event: TouchEvent): void {
    if (this.enableZoom && event.touches.length > 1) {
      const dx = event.touches[0].pageX - event.touches[1].pageX;
      const dy = event.touches[0].pageY - event.touches[1].pageY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      this.#dollyStart.set(0, distance);
    }
    if (this.enablePan) {
      const { x, y } = this.#getTouchXY(event.touches);
      this.#panStart.set(x, y);
    }
  }

  #handleTouchMoveDollyPan(event: TouchEvent): void {
    if (this.enableZoom && event.touches.length > 1) {
      const dx = event.touches[0].pageX - event.touches[1].pageX;
      const dy = event.touches[0].pageY - event.touches[1].pageY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      this.#dollyEnd.set(0, distance);
      this.#dollyDelta.set(0, Math.pow(this.#dollyEnd.y / this.#dollyStart.y, this.zoomSpeed));
      this.#dolly(this.#dollyDelta.y, undefined, undefined);
      this.#dollyStart.copy(this.#dollyEnd);
    }
    if (this.enablePan) {
      const { x, y } = this.#getTouchXY(event.touches);
      this.#panEnd.set(x, y);
      this.#panDelta.subVectors(this.#panEnd, this.#panStart).multiplyScalar(this.panSpeed);
      this.#pan(this.#panDelta.x, this.#panDelta.y);
      this.#panStart.copy(this.#panEnd);
    }
    this.update();
  }

  #onMouseDown(event: MouseEvent): void {
    if (this.enabled === false) {
      return;
    }
    if (event.button === MOUSE.LEFT && this.ignoreLeftClickWithoutCtrl && !event.ctrlKey) {
      return;
    }
    if (htmlElementIsGUI(event.target)) {
      return;
    }
    event.preventDefault();
    if (this.domElement?.focus) {
      this.domElement.focus();
    } else {
      window.focus();
    }
    switch (event.button) {
      case MOUSE.LEFT:
        if (this.enableRotate && event.altKey) {
          this.#handleMouseDownRotate(event);
          this.#state = STATE.ROTATE;
        } else if (event.ctrlKey) {
          if (this.enablePan === false) {
            return;
          }
          this.#handleMouseDownPan(event);
          this.#state = STATE.PAN;
        }
        break;
      case MOUSE.MIDDLE:
        if (this.enableZoom === false) {
          return;
        }
        this.#handleMouseDownDolly(event);
        this.#state = STATE.DOLLY;
        break;
      case MOUSE.RIGHT:
        if (this.enablePan === false) {
          return;
        }
        this.#handleMouseDownPan(event);
        this.#state = STATE.PAN;
        break;
    }
    if (this.#state !== STATE.NONE) {
      this.#removeDragListeners();
      this.#dragDocument = this.#getDragDocument();
      this.#dragDocument.addEventListener("mousemove", this.#onMouseMoveListener, false);
      this.#dragDocument.addEventListener("mouseup", this.#onMouseUpListener, false);
      this.dispatchEvent(START_EVENT);
    }
  }

  #getDragDocument(): Document {
    const domElement = this._domElement;
    return domElement.ownerDocument ?? (domElement as Document);
  }

  #removeDragListeners(): void {
    const dragDocument = this.#dragDocument ?? document;
    dragDocument.removeEventListener("mousemove", this.#onMouseMoveListener, false);
    dragDocument.removeEventListener("mouseup", this.#onMouseUpListener, false);
    this.#dragDocument = null;
  }

  #onMouseMove(event: MouseEvent): void {
    if (this.enabled === false) {
      return;
    }
    event.preventDefault();
    switch (this.#state) {
      case STATE.ROTATE:
        if (this.enableRotate === false) {
          return;
        }
        this.#handleMouseMoveRotate(event);
        break;
      case STATE.DOLLY:
        if (this.enableZoom === false) {
          return;
        }
        this.#handleMouseMoveDolly(event);
        break;
      case STATE.PAN:
        if (this.enablePan === false) {
          return;
        }
        this.#handleMouseMovePan(event);
        break;
    }
  }

  #onMouseUp(): void {
    this.#removeDragListeners();
    if (this.#state !== STATE.NONE) {
      this.dispatchEvent(END_EVENT);
      this.#state = STATE.NONE;
    }
  }

  #onMouseWheel(event: WheelEvent): void {
    if (
      this.enabled === false ||
      this.enableZoom === false ||
      (this.#state !== STATE.NONE && this.#state !== STATE.ROTATE)
    ) {
      return;
    }
    if (htmlElementIsGUI(event.target)) {
      return;
    }
    event.preventDefault();
    this.dispatchEvent(START_EVENT);
    this.#handleMouseWheel(event);
    this.dispatchEvent(END_EVENT);
  }

  #onTouchStart(event: TouchEvent): void {
    if (this.enabled === false) {
      return;
    }
    event.preventDefault();
    switch (event.touches.length) {
      case 1:
        if (this.enableZoom === false && this.enablePan === false) {
          return;
        }
        this.#handleTouchStartDollyPan(event);
        this.#state = STATE.TOUCH_DOLLY_PAN;
        break;
      case 2:
        if (this.enableZoom === false && this.enablePan === false) {
          return;
        }
        this.#handleTouchStartDollyPan(event);
        this.#state = STATE.TOUCH_DOLLY_PAN;
        break;
      default:
        this.#state = STATE.NONE;
    }
    if (this.#state !== STATE.NONE) {
      this.dispatchEvent(START_EVENT);
    }
  }

  #onTouchMove(event: TouchEvent): void {
    if (this.enabled === false) {
      return;
    }
    event.preventDefault();
    const cam = this.object as OrthographicCamera & { isOrthographicCamera?: boolean };
    switch (event.touches.length) {
      case 1:
        if (cam.isOrthographicCamera) {
          if (this.enableZoom === false && this.enablePan === false) {
            return;
          }
          if (this.#state !== STATE.TOUCH_DOLLY_PAN) {
            return;
          }
          this.#handleTouchMoveDollyPan(event);
        }
        break;
      case 2:
        if (this.enableZoom === false && this.enablePan === false) {
          return;
        }
        if (this.#state !== STATE.TOUCH_DOLLY_PAN) {
          return;
        }
        this.#handleTouchMoveDollyPan(event);
        break;
      default:
        this.#state = STATE.NONE;
    }
  }

  #onTouchEnd(): void {
    if (this.enabled === false) {
      return;
    }
    this.dispatchEvent(END_EVENT);
    this.#state = STATE.NONE;
  }

  #onContextMenu(event: MouseEvent): void {
    if (this.enabled === false) {
      return;
    }
    event.preventDefault();
  }
}
